import * as React from 'react'
// libs
import { useStore } from 'react-redux'
import { useRouter } from 'hooks/useRouter'
import { getFormValues } from 'redux-form'
import { useMutation, useQuery } from '@apollo/client/react'
// context
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
// constants
import { ENTITIES, Forms } from 'constants/index'
// hooks
import { useInitialValues, useDefaultObjectSecurity, useApolloClient } from 'hooks/index'
// utils
import get from 'lodash/get'
import pipe from 'lodash/fp/pipe'
import filter from 'lodash/fp/filter'
import isFunction from 'lodash/isFunction'
import { safeGetOr } from 'utils/fp'
import { handleError } from 'utils/errors/handlers'
import {
  getFieldValues,
  getOptionsByEntity,
  getValueFromOption,
  getValuesFromOptions,
  getFieldsInitialValues
} from 'utils/form'
import { runPendingUploads } from 'utils/pendingFileUploads'
// graphql
import { PLANS_DETAILS_QUERY } from 'graphql/plan/queries'
import { PLAN_UPDATE_MUTATION } from 'graphql/plan/mutations'
// types
import { IFieldValueMapItem, IPlan } from 'types/graphql'

type Props = React.PropsWithChildren<{
  planId?: string
}>

interface IContext {
  plan: IPlan
  handleSubmit: any
  initialValues: any
}

export const PlanDetailsContext = React.createContext<IContext>({} as any)

const PlanDetailsAPI: React.FC<Props> = ({ planId: planIdProp, children }) => {
  const router = useRouter()

  const planId = planIdProp ?? (router.query.id as string)

  const { data: plansQuery } = useQuery(PLANS_DETAILS_QUERY, {
    skip: !planId,
    variables: { filter: { id: planId } }
  })

  const [planUpdateMutation] = useMutation(PLAN_UPDATE_MUTATION)

  const { client } = useApolloClient()

  const { plan: currentPlan, treeQueryRefetch } = React.useContext(PlanEditPageContext)

  const plan = currentPlan || get(plansQuery, 'plans.data[0]')

  const defaultObjectSecurities = useDefaultObjectSecurity({ objectApiName: 'plans', planType: plan?.planType })

  const fieldValues = pipe(
    safeGetOr<any, any, any>([], 'fieldValueMap'),
    filter((value: IFieldValueMapItem) => value.entityId === plan?.planType.id)
  )(plan)

  const getInitialValues = async () => {
    const fieldsInitialValues = await getFieldsInitialValues({
      fieldValues,
      fields: get(plan, 'planType.fields', []),
      client
    })

    return { ...fieldsInitialValues }
  }

  const initialValues = useInitialValues(getInitialValues, plan)

  const store = useStore()

  // methods
  const handleSubmit = async (data, fields) => {
    await runPendingUploads(Forms.PLAN_EDIT_FORM)

    data = getFormValues(Forms.PLAN_EDIT_FORM)(store.getState()) || data

    try {
      if (isFunction(treeQueryRefetch)) {
        await treeQueryRefetch()
      }

      await planUpdateMutation({
        variables: {
          id: plan.id,
          input: {
            fieldValues: Array.isArray(fields)
              ? getFieldValues(fields, data, defaultObjectSecurities)
              : getFieldValues(plan.planType.fields, data, defaultObjectSecurities),
            memberPositionIds: getValuesFromOptions(getOptionsByEntity(data.membersUserPosition, ENTITIES.POSITION)),
            memberUserIds: getValuesFromOptions(getOptionsByEntity(data.membersUserPosition, ENTITIES.USER)),
            name: data.planTitle,
            ownedById: getValueFromOption(data.ownedByEmployeeOrPosition),
            planTypeId: getValueFromOption(data.planType),
            status: getValueFromOption(data.status)
          }
        },
        refetchQueries: [
          {
            query: PLANS_DETAILS_QUERY,
            variables: {
              filter: {
                id: planId
              }
            }
          }
        ]
      })
    } catch (errors) {
      handleError(errors, 'PlanDetails.handleSubmit')
    }
  }

  if (!plan) {
    return null
  }

  const value = {
    plan,
    handleSubmit,
    initialValues
  }
  return <PlanDetailsContext.Provider value={value}>{children}</PlanDetailsContext.Provider>
}

export default PlanDetailsAPI
